/**
 * The tenant's own vehicle categories, from the PMS.
 *
 * Replaces `ACRISS_TREE` in `@mocks/catalog`, a list of thirty-five
 * SIPP codes written by hand for the prototype. Measured against
 * canarias.com on 2026-09-28 it missed eight codes the engine is
 * repricing right now — CLAR, HCMR, IVAR, IVMR, MDAR, PVAR, UFAR,
 * UVAR — so no rule could be written for them, and it offered twenty
 * that do not exist in the fleet, which the operator had to scroll
 * past to find the ones that do.
 *
 * Every category the PMS knows is offered, mapped or not (Curro's
 * call). `mapped` rides along so the screen can say what an unmapped
 * one means: the rule is accepted and stored, and the engine never
 * acts on it, because consistency is evaluated against the ACRISS the
 * rate shops return.
 */
import { useEffect, useState } from 'react'
import { setupApi } from './api'

const EMPTY = { loading: true, items: [], tree: [], allSipps: [], error: null }

//: One request per page load, not one per component. The catalogue is
//: read by the Source column and by every chip in every group, which
//: is dozens of callers on a populated screen; without this each one
//: would fetch. A rejected promise is dropped so a failure can be
//: retried by the next mount rather than cached for ever.
let pending = null
function fetchOnce() {
	if (!pending) {
		pending = setupApi.categories()
		pending.catch(() => { pending = null })
	}
	return pending
}

export function useCategories() {
	const [state, setState] = useState(EMPTY)

	useEffect(() => {
		let alive = true
		fetchOnce()
			.then(items => {
				if (!alive) return
				setState({ loading: false, items: items || [], error: null,
				           tree: groupByClass(items || []),
				           allSipps: (items || []).map(c => c.sipp) })
			})
			.catch(e => {
				if (!alive) return
				// An empty picker with an explanation beats a picker full
				// of categories the tenant does not own.
				setState({ loading: false, items: [], tree: [], allSipps: [],
				           error: e?.message || 'Could not load the category catalogue' })
			})
		return () => { alive = false }
	}, [])

	return state
}

/**
 * `[{ code, name, sipps }]` — the shape the pickers already consume.
 *
 * The PMS's own `vehicle_class` is the grouping; it is presentation
 * only, exactly as the macro was, and the operational unit stays the
 * SIPP. Order is the server's, which is the PMS's price-list order.
 */
export function groupByClass(items) {
	const out = []
	const byClass = new Map()
	for (const c of items) {
		const key = c.vehicle_class || 'Unknown'
		if (!byClass.has(key)) {
			const macro = { code: key.toLowerCase(), name: key, sipps: [] }
			byClass.set(key, macro)
			out.push(macro)
		}
		byClass.get(key).sipps.push(c.sipp)
	}
	return out
}
